import { useCallback, useContext, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Layer, Source } from 'react-map-gl/mapbox';
import Typography from '@alltrails/core/components/Typography';
import { COLOR_NEUTRAL_WHITE, SPACE_50 } from '@alltrails/denali/tokens';
import useMap from '../../hooks/useMap';
import { useAppDispatch } from '../../redux';
import { updateCursor } from '../../redux/reducer';
import PopupOffset from '../../types/PopupOffset';
import { defaultSymbolLayerLayoutProps, getLayerBeforeId, tapToRemoveMarkerId } from '../../utils/layers';
import { createTargetMarker, targetSize } from '../../utils/svgDefinitions/svgMarkerComponents';
import useSvgImages from '../../utils/useSvgImages';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import PaddingAwarePopup from '../PaddingAwarePopup';
import styles from './styles/styles.module.scss';

const sourceId = 'tap-to-remove-marker';
const svgDefinitions = [createTargetMarker(tapToRemoveMarkerId, COLOR_NEUTRAL_WHITE)];

const popupOffset: PopupOffset = {
  bottom: [0, targetSize / -2 - SPACE_50],
  'bottom-left': [targetSize / -2, targetSize / -2 - SPACE_50],
  'bottom-right': [targetSize / 2, targetSize / -2 - SPACE_50],
  top: [0, targetSize / 2 + SPACE_50],
  'top-left': [targetSize / -2, targetSize / 2 + SPACE_50],
  'top-right': [targetSize / 2, targetSize / 2 + SPACE_50],
  left: [targetSize / 2 + SPACE_50, 0],
  right: [targetSize / -2 - SPACE_50, 0]
};

type TapToRemoveMarkerProps = {
  latitude: number;
  longitude: number;
  onRemove: () => void;
  // The tooltip is controlled externally so that consumers can decide to show the tooltip on initial render or not
  setShowTooltip: (showTooltip: boolean) => void;
  showTooltip: boolean;
};

const TapToRemoveMarker = ({ latitude, longitude, onRemove, setShowTooltip, showTooltip }: TapToRemoveMarkerProps) => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  const onClick = useCallback(() => {
    onRemove();
  }, [onRemove]);

  const onMouseMove = useCallback(() => {
    setShowTooltip(true);
    dispatch(updateCursor('pointer'));
  }, [dispatch, setShowTooltip]);

  const onMouseLeave = useCallback(() => {
    setShowTooltip(false);
    dispatch(updateCursor('unset'));
  }, [dispatch, setShowTooltip]);

  useEffect(() => {
    registerEventHandler(tapToRemoveMarkerId, 'click', onClick);
    registerEventHandler(tapToRemoveMarkerId, 'mousemove', onMouseMove);
    registerEventHandler(tapToRemoveMarkerId, 'mouseleave', onMouseLeave);
    return () => {
      unregisterEventHandler(tapToRemoveMarkerId, 'click', onClick);
      unregisterEventHandler(tapToRemoveMarkerId, 'mousemove', onMouseMove);
      unregisterEventHandler(tapToRemoveMarkerId, 'mouseleave', onMouseLeave);
    };
  }, [onClick, onMouseLeave, onMouseMove, registerEventHandler, unregisterEventHandler]);

  const popup = useMemo(() => {
    if (showTooltip) {
      return (
        <PaddingAwarePopup
          className={styles.popup}
          closeButton={false}
          closeOnClick={false}
          focusAfterOpen={false}
          latitude={latitude}
          longitude={longitude}
          maxWidth="none"
          offset={popupOffset}
        >
          <div className={styles.popupContainer}>
            <Typography color="inverse" variant="text100">
              <FormattedMessage defaultMessage="Tap to remove" />
            </Typography>
          </div>
        </PaddingAwarePopup>
      );
    }
    return null;
  }, [latitude, longitude, showTooltip]);

  if (!imagesAreLoaded) {
    return null;
  }

  return (
    <Source id={sourceId} type="geojson" data={{ type: 'Point', coordinates: [longitude, latitude] }}>
      <Layer
        id={tapToRemoveMarkerId}
        source={sourceId}
        beforeId={getLayerBeforeId(map, tapToRemoveMarkerId)}
        type="symbol"
        layout={{
          ...defaultSymbolLayerLayoutProps,
          'icon-allow-overlap': true, // Always want this to show up
          'icon-image': tapToRemoveMarkerId
        }}
      />
      {popup}
    </Source>
  );
};

export default TapToRemoveMarker;
